import React, { useState } from "react";
import { Close, Check } from "@mui/icons-material";
import apiService from "../services/apiService";
import { toast } from "react-toastify";

const SubmitResolutionModal = ({ loan, onClose, onSubmitSuccess }) => {
  const [resolutionType, setResolutionType] = useState("");
  const [notes, setNotes] = useState("");
  const [promiseDate, setPromiseDate] = useState("");
  const [promiseAmount, setPromiseAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [nextLoanOption, setNextLoanOption] = useState("next"); // 'next' or 'close'

  const resolutionTypes = [
    { value: "client_picked_up", label: "Client Picked Up", color: "bg-green-100 text-green-800" },
    { value: "client_no_answer", label: "Client Didn't Pick Up", color: "bg-yellow-100 text-yellow-800" },
    { value: "promise_to_pay", label: "Promise to Pay", color: "bg-blue-100 text-blue-800" },
    { value: "payment_collected", label: "Payment Collected", color: "bg-green-100 text-green-800" },
    { value: "resolved", label: "Resolved", color: "bg-purple-100 text-purple-800" },
    { value: "wrong_number", label: "Wrong Number", color: "bg-red-100 text-red-800" },
    { value: "dispute", label: "Dispute", color: "bg-orange-100 text-orange-800" },
    { value: "other", label: "Other", color: "bg-gray-100 text-gray-800" }
  ];

  const handleSubmit = async () => {
    // Validation
    if (!resolutionType) {
      toast.error("Please select a resolution type");
      return;
    }

    // Notes not required for 'resolved' and 'client_no_answer' types
    const notesOptionalTypes = ["resolved", "client_no_answer"];
    if (!notesOptionalTypes.includes(resolutionType) && !notes.trim()) {
      toast.error("Please add notes");
      return;
    }

    if (resolutionType === "promise_to_pay") {
      if (!promiseDate) {
        toast.error("Please select a promise date");
        return;
      }
      if (!promiseAmount || parseFloat(promiseAmount) <= 0) {
        toast.error("Please enter a valid promise amount");
        return;
      }
    }

    if (resolutionType === "payment_collected") {
      if (!promiseAmount || parseFloat(promiseAmount) <= 0) {
        toast.error("Please enter the amount collected");
        return;
      }
    }

    setSubmitting(true);

    try {
      const response = await apiService.post(
        `${process.env.REACT_APP_API_HOST_COLLECTIONS}/submit-resolution`,
        {
          loanId: loan._id,
          clientId: loan.client._id,
          resolutionType,
          notes,
          promiseDate: resolutionType === "promise_to_pay" ? promiseDate : undefined,
          promiseAmount: ["promise_to_pay", "payment_collected"].includes(resolutionType)
            ? parseFloat(promiseAmount)
            : undefined
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success("Resolution submitted successfully");

        // Call success handler with navigation option
        onSubmitSuccess({
          shouldNavigateToNext: nextLoanOption === "next",
          nextLoanId: data.nextLoanId
        });
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || "Failed to submit resolution");
      }
    } catch (error) {
      console.error("Error submitting resolution:", error);
      toast.error("An error occurred while submitting resolution");
    } finally {
      setSubmitting(false);
    }
  };

  const selectedType = resolutionTypes.find(t => t.value === resolutionType);

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="border-b p-6 flex justify-between items-center bg-orange-50">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Submit Collection Resolution</h2>
            <p className="text-sm text-gray-600 mt-1">
              Loan #{loan.loanNumber} - {loan.client?.personal?.fullName}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            disabled={submitting}
          >
            <Close />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1 space-y-6">
          {/* Resolution Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Resolution Type <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              {resolutionTypes.map((type) => (
                <button
                  key={type.value}
                  onClick={() => setResolutionType(type.value)}
                  className={`p-3 rounded-lg border-2 transition-all text-left ${
                    resolutionType === type.value
                      ? `${type.color} border-current`
                      : "bg-white border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      resolutionType === type.value ? "border-current" : "border-gray-300"
                    }`}>
                      {resolutionType === type.value && (
                        <Check fontSize="small" className="text-current" style={{ fontSize: 16 }} />
                      )}
                    </div>
                    <span className="font-medium text-sm">{type.label}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Promise to Pay Fields */}
          {resolutionType === "promise_to_pay" && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Promise Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={promiseDate}
                    onChange={(e) => setPromiseDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Promise Amount <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={promiseAmount}
                    onChange={(e) => setPromiseAmount(e.target.value)}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Payment Collected Fields */}
          {resolutionType === "payment_collected" && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Amount Collected <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                value={promiseAmount}
                onChange={(e) => setPromiseAmount(e.target.value)}
                placeholder="0.00"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-2">
                The amount the client paid. Used in collection totals and reports.
              </p>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes {!["resolved", "client_no_answer"].includes(resolutionType) && <span className="text-red-500">*</span>}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={
                ["resolved", "client_no_answer"].includes(resolutionType)
                  ? "Enter notes about this call/interaction (optional)..."
                  : "Enter detailed notes about this call/interaction..."
              }
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
            />
            <p className="text-xs text-gray-500 mt-1">
              {notes.length} characters
            </p>
          </div>

          {/* Next Action */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              After submitting, what would you like to do?
            </label>
            <div className="space-y-2">
              <button
                onClick={() => setNextLoanOption("next")}
                className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                  nextLoanOption === "next"
                    ? "bg-green-50 border-green-500"
                    : "bg-white border-gray-200 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center gap-2">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    nextLoanOption === "next" ? "border-green-500" : "border-gray-300"
                  }`}>
                    {nextLoanOption === "next" && (
                      <Check fontSize="small" className="text-green-600" style={{ fontSize: 16 }} />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-900">Go to Next Loan</p>
                    <p className="text-xs text-gray-600">Automatically load the next assigned loan</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => setNextLoanOption("close")}
                className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                  nextLoanOption === "close"
                    ? "bg-blue-50 border-blue-500"
                    : "bg-white border-gray-200 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center gap-2">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    nextLoanOption === "close" ? "border-blue-500" : "border-gray-300"
                  }`}>
                    {nextLoanOption === "close" && (
                      <Check fontSize="small" className="text-blue-600" style={{ fontSize: 16 }} />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-900">Stay on This Loan</p>
                    <p className="text-xs text-gray-600">Close this dialog and review the loan details</p>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex justify-end gap-3 bg-gray-50">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Submitting...
              </>
            ) : (
              "Submit Resolution"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubmitResolutionModal;
